import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "xamseen-theme";

const MODES: ThemeMode[] = ["system", "light", "dark"];

export function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === "string" && MODES.includes(value as ThemeMode);
}

/** Inlined in the document head so the correct theme paints before hydration. */
export const themeBootstrapScript = `(function(){try{var k="${THEME_STORAGE_KEY}";var m=localStorage.getItem(k);if(m!=="light"&&m!=="dark"&&m!=="system"){m="system"}var d=m==="dark"||(m==="system"&&window.matchMedia("(prefers-color-scheme: dark)").matches);var e=document.documentElement;e.classList.toggle("dark",d);e.style.colorScheme=d?"dark":"light";e.dataset.theme=m}catch(_){}})();`;

function systemPrefersDark() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyTheme(mode: ThemeMode, systemDark: boolean) {
  if (typeof document === "undefined") return;
  const dark = mode === "dark" || (mode === "system" && systemDark);
  const el = document.documentElement;
  el.classList.toggle("dark", dark);
  el.style.colorScheme = dark ? "dark" : "light";
  el.dataset.theme = mode;
}

interface ThemeContextValue {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Xamseen follows the device by default: light phones get the white
  // interface, dark phones get the charcoal one. Light/Dark are explicit opt-ins.
  const [mode, setModeState] = useState<ThemeMode>("system");
  const [systemDark, setSystemDark] = useState(false);

  // Hydrate from the local fallback + follow OS changes.
  useEffect(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeMode(stored)) setModeState(stored);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    setSystemDark(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Cross-device preference from the authenticated profile, when available.
  useEffect(() => {
    let active = true;
    const sync = async (userId: string) => {
      const { data } = await supabase
        .from("profiles")
        .select("theme_preference")
        .eq("id", userId)
        .maybeSingle();
      const remote = (data as { theme_preference?: string } | null)?.theme_preference;
      if (!active || !isThemeMode(remote)) return;
      setModeState(remote);
      localStorage.setItem(THEME_STORAGE_KEY, remote);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) sync(session.user.id);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) setTimeout(() => sync(session.user.id), 0);
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, []);

  useEffect(() => { applyTheme(mode, systemDark); }, [mode, systemDark]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try { localStorage.setItem(THEME_STORAGE_KEY, next); } catch { /* private mode */ }
    applyTheme(next, systemPrefersDark());
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return;
      void supabase
        .from("profiles")
        .update({ theme_preference: next } as never)
        .eq("id", session.user.id);
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      resolved: (mode === "system" ? (systemDark ? "dark" : "light") : mode) as ResolvedTheme,
      setMode,
    }),
    [mode, systemDark, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
